import { RunElement } from '../../shared/run-element.js';
import { skinResolutionMixin } from '../../shared/skin-resolution-mixin.js';
import { loadSkinModule } from '../../shared/skin-loader.js';
import * as defaultSkin from './skins/input.default.js';

class RunInput extends skinResolutionMixin(RunElement) {
  // Default skin is always bundled; all others lazy-load via the registry.
  // boston-bricks routes through loadSkinModule() to keep the Vite
  // asset-prebundling workaround intact.
  static defaultSkin = defaultSkin;
  static skinLoaders = {
    wireframe: () => import('./skins/input.wireframe.js'),
    minimal: () => import('./skins/input.minimal.js'),
    ornate: () => import('./skins/input.ornate.js'),
    bento: () => import('./skins/input.bento.js'),
    'boston-bricks': () => loadSkinModule('boston-bricks', 'input'),
  };

  // -------------------------------------------------------------------------
  // Private field declarations
  // -------------------------------------------------------------------------
  #value = '';
  #defaultValue = '';
  #name = '';
  #type = 'text';
  #placeholder = '';
  #disabled = false;
  #readonly = false;
  #required = false;
  #autocomplete = 'off';
  #minlength = null;
  #maxlength = null;
  #min = null;
  #max = null;
  #step = null;
  #pattern = null;
  #label = '';
  #helpText = '';
  #errorMessage = '';
  #hideLabel = false;
  #invalid = false;
  #size = 'md';
  #skin = null;
  #dirty = false;

  #internals = null;
  #handleInput = null;
  #handleChange = null;
  #handleFocusin = null;
  #handleFocusout = null;

  // -------------------------------------------------------------------------
  // Static class fields
  // -------------------------------------------------------------------------
  static formAssociated = true;
  static componentName = 'Input';
  static version = '1.0.0';
  static category = 'form';
  static layer = 'atomic';
  static parts = ['base', 'label', 'field-wrapper', 'input', 'prefix', 'suffix', 'support'];
  static slots = [
    { name: 'prefix', description: 'Leading adornment (icon, currency symbol)' },
    { name: 'suffix', description: 'Trailing adornment (icon, unit label, clear button)' },
  ];
  static events = [
    { name: 'run-input', description: 'Fired on user input (keystrokes)' },
    { name: 'run-change', description: 'Fired when user commits value' },
  ];

  // -------------------------------------------------------------------------
  // observedAttributes
  // -------------------------------------------------------------------------
  static get observedAttributes() {
    return [
      'value', 'name', 'type', 'placeholder', 'disabled', 'readonly', 'required',
      'autocomplete', 'minlength', 'maxlength', 'min', 'max', 'step', 'pattern',
      'label', 'help-text', 'error-message', 'invalid', 'size', 'skin', 'hide-label',
    ];
  }

  // -------------------------------------------------------------------------
  // constructor
  // -------------------------------------------------------------------------
  constructor() {
    super();
    this.#internals = this.attachInternals();
    this.#handleInput = this.#onInput.bind(this);
    this.#handleChange = this.#onChange.bind(this);
    this.#handleFocusin = this.#onFocusin.bind(this);
    this.#handleFocusout = this.#onFocusout.bind(this);
  }

  // -------------------------------------------------------------------------
  // connectedCallback
  // -------------------------------------------------------------------------
  connectedCallback() {
    super.connectedCallback?.();
    this.addEventListener('input', this.#handleInput);
    this.addEventListener('change', this.#handleChange);
    this.addEventListener('focusin', this.#handleFocusin);
    this.addEventListener('focusout', this.#handleFocusout);
    this.#syncFormValue();
    this.#updateValidity();
    // Headless base provides --_instance-seed for boundary skin variation.
    // Set once at connection time; ordinal-based skins can use CSS :nth-child instead.
    if (!this.style.getPropertyValue('--_instance-seed')) {
      this.style.setProperty('--_instance-seed', String(Math.random()));
    }
  }

  // -------------------------------------------------------------------------
  // disconnectedCallback
  // -------------------------------------------------------------------------
  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('input', this.#handleInput);
    this.removeEventListener('change', this.#handleChange);
    this.removeEventListener('focusin', this.#handleFocusin);
    this.removeEventListener('focusout', this.#handleFocusout);
  }

  // -------------------------------------------------------------------------
  // attributeChangedCallback
  // -------------------------------------------------------------------------
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'value':         this.value        = newValue === null ? '' : newValue; break;
      case 'name':          this.name         = newValue === null ? '' : newValue; break;
      case 'type':          this.type         = newValue === null ? 'text' : newValue; break;
      case 'placeholder':   this.placeholder  = newValue === null ? '' : newValue; break;
      case 'disabled':      this.disabled     = newValue !== null; break;
      case 'readonly':      this.readonly     = newValue !== null; break;
      case 'required':      this.required     = newValue !== null; break;
      case 'autocomplete':  this.autocomplete = newValue === null ? 'off' : newValue; break;
      case 'minlength':     this.minlength    = newValue === null ? null : Number(newValue); break;
      case 'maxlength':     this.maxlength    = newValue === null ? null : Number(newValue); break;
      case 'min':           this.min          = newValue === null ? null : newValue; break;
      case 'max':           this.max          = newValue === null ? null : newValue; break;
      case 'step':          this.step         = newValue === null ? null : newValue; break;
      case 'pattern':       this.pattern      = newValue === null ? null : newValue; break;
      case 'label':         this.label        = newValue === null ? '' : newValue; break;
      case 'help-text':     this.helpText     = newValue === null ? '' : newValue; break;
      case 'error-message': this.errorMessage = newValue === null ? '' : newValue; break;
      case 'invalid':       this.invalid      = newValue !== null; break;
      case 'size':          this.size         = newValue === null ? 'md' : newValue; break;
      case 'skin':          this.skin         = newValue; break;
      case 'hide-label':    this.hideLabel    = newValue !== null; break;
    }
  }

  // -------------------------------------------------------------------------
  // Form lifecycle callbacks
  // -------------------------------------------------------------------------
  formResetCallback() {
    this.#value = this.#defaultValue;
    if (this.#defaultValue !== '') {
      this.setAttribute('value', this.#defaultValue);
    } else {
      this.removeAttribute('value');
    }
    this.#invalid = false;
    this.removeAttribute('invalid');
    this.removeAttribute('data-invalid');
    this.#errorMessage = '';
    this.removeAttribute('error-message');
    this.#dirty = false;
    this.removeAttribute('data-dirty');
    this.#syncFormValue();
    this.#updateValidity();
    this.requestUpdate();
  }

  formDisabledCallback(disabled) {
    this.disabled = disabled;
  }

  formStateRestoreCallback(state) {
    if (typeof state === 'string') {
      this.value = state;
    }
  }

  // -------------------------------------------------------------------------
  // Public property accessors
  // -------------------------------------------------------------------------

  get skin() { return this.#skin; }
  set skin(value) {
    const trimmed = value == null ? '' : String(value).trim();
    const coerced = trimmed || null;
    if (this.#skin === coerced) return;
    this.#skin = coerced;
    if (coerced) this.setAttribute('skin', coerced);
    else this.removeAttribute('skin');
    this.requestUpdate();
  }

  get validity() { return this.#internals.validity; }

  get value() { return this.#value; }
  set value(raw) {
    const coerced = String(raw);
    if (this.#value === coerced) return;
    this.#value = coerced;
    if (coerced) this.setAttribute('value', coerced);
    else this.removeAttribute('value');
    this.#syncFormValue();
    this.#updateValidity();
    this.requestUpdate();
  }

  get name() { return this.#name; }
  set name(raw) {
    const coerced = raw == null ? '' : String(raw).trim();
    if (this.#name === coerced) return;
    this.#name = coerced;
    if (coerced) this.setAttribute('name', coerced);
    else this.removeAttribute('name');
    this.requestUpdate();
  }

  get type() { return this.#type; }
  set type(raw) {
    const allowed = ['text', 'email', 'password', 'number', 'search', 'tel', 'url'];
    const coerced = raw == null ? 'text' : String(raw);
    const resolved = allowed.includes(coerced) ? coerced : 'text';
    if (this.#type === resolved) return;
    this.#type = resolved;
    this.setAttribute('type', resolved);
    this.requestUpdate();
  }

  get placeholder() { return this.#placeholder; }
  set placeholder(raw) {
    const coerced = raw == null ? '' : String(raw);
    if (this.#placeholder === coerced) return;
    this.#placeholder = coerced;
    if (coerced) this.setAttribute('placeholder', coerced);
    else this.removeAttribute('placeholder');
    this.requestUpdate();
  }

  get disabled() { return this.#disabled; }
  set disabled(raw) {
    const coerced = Boolean(raw);
    if (this.#disabled === coerced) return;
    this.#disabled = coerced;
    this.toggleAttribute('disabled', coerced);
    this.toggleAttribute('data-disabled', coerced);
    this.requestUpdate();
  }

  get readonly() { return this.#readonly; }
  set readonly(raw) {
    const coerced = Boolean(raw);
    if (this.#readonly === coerced) return;
    this.#readonly = coerced;
    this.toggleAttribute('readonly', coerced);
    this.toggleAttribute('data-readonly', coerced);
    this.requestUpdate();
  }

  get required() { return this.#required; }
  set required(raw) {
    const coerced = Boolean(raw);
    if (this.#required === coerced) return;
    this.#required = coerced;
    this.toggleAttribute('required', coerced);
    this.toggleAttribute('data-required', coerced);
    this.#updateValidity();
    this.requestUpdate();
  }

  get autocomplete() { return this.#autocomplete; }
  set autocomplete(raw) {
    const coerced = raw == null ? 'off' : String(raw);
    if (this.#autocomplete === coerced) return;
    this.#autocomplete = coerced;
    this.setAttribute('autocomplete', coerced);
    this.requestUpdate();
  }

  get minlength() { return this.#minlength; }
  set minlength(raw) {
    const n = raw == null ? -1 : Number(raw);
    const coerced = n >= 0 ? n : null;
    if (this.#minlength === coerced) return;
    this.#minlength = coerced;
    if (coerced !== null) this.setAttribute('minlength', String(coerced));
    else this.removeAttribute('minlength');
    this.#updateValidity();
    this.requestUpdate();
  }

  get maxlength() { return this.#maxlength; }
  set maxlength(raw) {
    const n = raw == null ? -1 : Number(raw);
    const coerced = n >= 0 ? n : null;
    if (this.#maxlength === coerced) return;
    this.#maxlength = coerced;
    if (coerced !== null) this.setAttribute('maxlength', String(coerced));
    else this.removeAttribute('maxlength');
    this.#updateValidity();
    this.requestUpdate();
  }

  get min() { return this.#min; }
  set min(raw) {
    const str = raw == null ? '' : String(raw).trim();
    const coerced = str || null;
    if (this.#min === coerced) return;
    this.#min = coerced;
    if (coerced) this.setAttribute('min', coerced);
    else this.removeAttribute('min');
    this.#updateValidity();
    this.requestUpdate();
  }

  get max() { return this.#max; }
  set max(raw) {
    const str = raw == null ? '' : String(raw).trim();
    const coerced = str || null;
    if (this.#max === coerced) return;
    this.#max = coerced;
    if (coerced) this.setAttribute('max', coerced);
    else this.removeAttribute('max');
    this.#updateValidity();
    this.requestUpdate();
  }

  get step() { return this.#step; }
  set step(raw) {
    const str = raw == null ? '' : String(raw).trim();
    const coerced = str || null;
    if (this.#step === coerced) return;
    this.#step = coerced;
    if (coerced) this.setAttribute('step', coerced);
    else this.removeAttribute('step');
    this.requestUpdate();
  }

  get pattern() { return this.#pattern; }
  set pattern(raw) {
    const str = raw == null ? '' : String(raw).trim();
    const coerced = str || null;
    if (this.#pattern === coerced) return;
    this.#pattern = coerced;
    if (coerced) this.setAttribute('pattern', coerced);
    else this.removeAttribute('pattern');
    this.#updateValidity();
    this.requestUpdate();
  }

  get label() { return this.#label; }
  set label(raw) {
    const coerced = raw == null ? '' : String(raw).trim();
    if (this.#label === coerced) return;
    this.#label = coerced;
    if (coerced) this.setAttribute('label', coerced);
    else this.removeAttribute('label');
    this.requestUpdate();
  }

  get helpText() { return this.#helpText; }
  set helpText(raw) {
    const coerced = raw == null ? '' : String(raw).trim();
    if (this.#helpText === coerced) return;
    this.#helpText = coerced;
    if (coerced) this.setAttribute('help-text', coerced);
    else this.removeAttribute('help-text');
    this.requestUpdate();
  }

  get errorMessage() { return this.#errorMessage; }
  set errorMessage(raw) {
    const coerced = raw == null ? '' : String(raw).trim();
    if (this.#errorMessage === coerced) return;
    this.#errorMessage = coerced;
    if (coerced) this.setAttribute('error-message', coerced);
    else this.removeAttribute('error-message');
    this.#updateValidity();
    this.requestUpdate();
  }

  get hideLabel() { return this.#hideLabel; }
  set hideLabel(raw) {
    const coerced = Boolean(raw);
    if (this.#hideLabel === coerced) return;
    this.#hideLabel = coerced;
    this.toggleAttribute('hide-label', coerced);
    this.requestUpdate();
  }

  get invalid() { return this.#invalid; }
  set invalid(raw) {
    const coerced = Boolean(raw);
    if (this.#invalid === coerced) return;
    this.#invalid = coerced;
    this.toggleAttribute('invalid', coerced);
    this.toggleAttribute('data-invalid', coerced);
    this.#updateValidity();
    this.requestUpdate();
  }

  get size() { return this.#size; }
  set size(raw) {
    const allowed = ['sm', 'md', 'lg'];
    const coerced = raw == null ? 'md' : String(raw);
    const resolved = allowed.includes(coerced) ? coerced : 'md';
    if (this.#size === resolved) return;
    this.#size = resolved;
    this.setAttribute('size', resolved);
    this.requestUpdate();
  }

  // -------------------------------------------------------------------------
  // Public methods
  // -------------------------------------------------------------------------
  focus() {
    const inner = this.shadowRoot?.querySelector('input');
    if (inner) inner.focus(); else super.focus?.();
  }

  blur() {
    const inner = this.shadowRoot?.querySelector('input');
    if (inner) inner.blur(); else super.blur?.();
  }

  select() {
    this.shadowRoot?.querySelector('input')?.select();
  }

  checkValidity() {
    return this.#internals.checkValidity();
  }

  reportValidity() {
    return this.#internals.reportValidity();
  }

  setCustomValidity(message) {
    const msg = message == null ? '' : String(message);
    if (msg === '') {
      this.#internals.setValidity({});
    } else {
      this.#internals.setValidity({ customError: true }, msg);
    }
  }

  // -------------------------------------------------------------------------
  // Skin rendering
  // -------------------------------------------------------------------------

  updated(changedProperties) {
    super.updated?.(changedProperties);

    // Skin DOM + stylesheet are delegated to the resolved skin module by
    // skinResolutionMixin._render(), which runs before updated(). The
    // post-skin slot/boundary work below observes the rendered skin DOM.

    // Detect slot content after skin renders (slot assignment is synchronous)
    const sr = this.shadowRoot;
    const wrapper = sr.querySelector('[part="field-wrapper"]');
    if (wrapper) {
      const prefixSlot = sr.querySelector('slot[name="prefix"]');
      const suffixSlot = sr.querySelector('slot[name="suffix"]');
      wrapper.toggleAttribute('data-has-prefix',
        (prefixSlot?.assignedNodes({ flatten: true }).length ?? 0) > 0);
      wrapper.toggleAttribute('data-has-suffix',
        (suffixSlot?.assignedNodes({ flatten: true }).length ?? 0) > 0);
    }

    this.renderBoundary();
  }

  // -------------------------------------------------------------------------
  // Extension API (Component Extension API Contract v1.0)
  // -------------------------------------------------------------------------

  /**
   * Returns the boundary element for this component.
   * Called by updated() after the skin renders. Override in subclasses to
   * apply full-skin composed-mode boundary rendering that CSS alone cannot express.
   *
   * Composition context available inside an override:
   *   this.hasAttribute('data-ys-composed') — true when parent claims this boundary
   *   this.getAttribute('data-position')    — 'start' | 'end' | null
   *
   * @returns {HTMLElement | null}
   */
  renderBoundary() {
    return this.shadowRoot?.querySelector('[part="field-wrapper"]') ?? null;
  }

  // -------------------------------------------------------------------------
  // Private methods
  // -------------------------------------------------------------------------
  #syncFormValue() {
    this.#internals.setFormValue(this.#value);
  }

  #updateValidity() {
    const validity = {};
    const messages = [];

    if (this.#required && this.#value === '') {
      validity.valueMissing = true;
      messages.push('This field is required.');
    }

    if (
      this.#minlength !== null &&
      this.#value.length > 0 &&
      this.#value.length < this.#minlength
    ) {
      validity.tooShort = true;
      messages.push(`Minimum length is ${this.#minlength} characters.`);
    }

    if (this.#maxlength !== null && this.#value.length > this.#maxlength) {
      validity.tooLong = true;
      messages.push(`Maximum length is ${this.#maxlength} characters.`);
    }

    if (this.#pattern && this.#value !== '') {
      try {
        const re = new RegExp(`^(?:${this.#pattern})$`);
        if (!re.test(this.#value)) {
          validity.patternMismatch = true;
          messages.push('Please match the requested format.');
        }
      } catch (_) {
        // Invalid pattern — skip constraint
      }
    }

    if (this.#type === 'number' && this.#value !== '') {
      const num = parseFloat(this.#value);
      if (!isNaN(num)) {
        if (this.#min !== null && num < parseFloat(this.#min)) {
          validity.rangeUnderflow = true;
          messages.push(`Value must be ${this.#min} or more.`);
        }
        if (this.#max !== null && num > parseFloat(this.#max)) {
          validity.rangeOverflow = true;
          messages.push(`Value must be ${this.#max} or less.`);
        }
      }
    }

    if (this.#invalid && this.#errorMessage) {
      validity.customError = true;
      messages.push(this.#errorMessage);
    }

    if (Object.keys(validity).length === 0) {
      this.#internals.setValidity({});
    } else {
      this.#internals.setValidity(validity, messages[0]);
    }
  }

  #onInput(event) {
    const inner = event.composedPath().find(el => el.tagName === 'INPUT');
    if (!inner) return;
    const next = inner.value;
    if (this.#value === next) return;
    this.#value = next;
    if (!this.#dirty) {
      this.#dirty = true;
      this.setAttribute('data-dirty', '');
    }
    this.#syncFormValue();
    this.#updateValidity();
    this.requestUpdate();
    this.#emitInput();
  }

  #onChange(event) {
    const inner = event.composedPath().find(el => el.tagName === 'INPUT');
    if (!inner) return;
    const next = inner.value;
    if (this.#value !== next) {
      this.#value = next;
      this.#syncFormValue();
      this.#updateValidity();
    }
    this.#emitChange();
  }

  #onFocusin() {
    this.setAttribute('data-focused', '');
    this.requestUpdate();
  }

  #onFocusout() {
    this.removeAttribute('data-focused');
    this.requestUpdate();
  }

  #emitInput() {
    this.dispatchEvent(new CustomEvent('run-input', {
      bubbles: true,
      composed: true,
      detail: { value: this.#value },
    }));
  }

  #emitChange() {
    this.dispatchEvent(new CustomEvent('run-change', {
      bubbles: true,
      composed: true,
      detail: { value: this.#value },
    }));
  }
}

customElements.define('run-input', RunInput);

export const INPUT_TYPES = Object.freeze(['text', 'email', 'password', 'number', 'search', 'tel', 'url']);
export const INPUT_SIZES = Object.freeze(['sm', 'md', 'lg']);
